/**
 * offlineQueueDB.js — IndexedDB foundation for A6 Sub-Phase 3.
 *
 * Wraps Mozilla's idb library (Promise-based IndexedDB wrapper) to provide
 * a typed, versioned, multi-store database for offline write queueing.
 *
 * THIS FILE CONTAINS:
 *   - Schema definition (3 object stores + indexes + versioning) [Step 1+2]
 *   - openDB() function with onUpgradeNeeded migration logic   [Step 1+2]
 *   - JSDoc typedefs for Queue-Entry-Shape                     [Step 1+2]
 *   - Constants for store names + index names                  [Step 1+2]
 *   - FIFO operations (enqueue/dequeue/peek/getDepth/moveTo)  [Step 3]
 *   - Ergonomic wrappers (markCompleted/markFailed/markConflict) [Step 3]
 *
 * NOT YET IN THIS FILE:
 *   - BroadcastChannel multi-tab coordination — Step 4
 *   - Quota / corruption recovery — Step 4
 *   - apiClient integration — Step 6
 *
 * Schema versioning: DB_VERSION must be incremented for every breaking change.
 * Migration logic in upgradeDatabase() handles old -> new schema transitions.
 *
 * Authority for design:
 *   - A6 detail file: docs/roadmap/A6_offline_first_sync_engine.md
 *   - A6-12: Schema with 3 object stores (queue, completed, conflicts)
 *   - A6-14: FIFO ordering within (tenantId, userId)
 *
 * Browser support: All modern browsers (Chrome/Safari/Firefox/Edge).
 *   IndexedDB API since 2013. idb library: ~1KB gzip.
 */

import { openDB } from 'idb';

// Step 4 (A6 Sub-Phase 3): Quota gate. enqueue() asserts capacity before put.
// Other Step 4 modules (queueLeader, queueRecovery) are not imported here —
// they are independently activated by app lifecycle (main.jsx hooks them up).
import { assertHasCapacity } from './queueQuota.js';

// =============================================================================
// Database Identity + Versioning
// =============================================================================

/**
 * Database name. Singular per-origin (chat.airfarm.io has ONE aif_offline_queue).
 * Different origins (e.g. staging) get their own isolated DB.
 */
export const DB_NAME = 'aif_offline_queue';

/**
 * Schema version. Increment for every breaking schema change.
 * upgradeDatabase() in openDatabase() handles migration from older versions.
 *
 * Version history:
 *   v1 (2026-05-21): Initial schema. 3 object stores, FIFO indexes.
 */
export const DB_VERSION = 2;  // v2 (A6 Sub-Phase 4 Session 3): adds uuid_mappings store

// =============================================================================
// Object Store Names (canonical constants — never hardcode strings elsewhere)
// =============================================================================

/**
 * Object store names. Use these constants to avoid typos in operations code.
 */
export const STORES = Object.freeze({
  /**
   * pending_writes: Writes that have not yet been flushed to the server.
   * FIFO order within (tenantId, userId) scope.
   * Sub-Phase 4 sync engine drains this store.
   */
  PENDING: 'pending_writes',

  /**
   * completed_writes: Writes that have been successfully synced to server.
   * Kept for auditing and as anti-replay reference. TTL cleanup in Sub-Phase 4.
   */
  COMPLETED: 'completed_writes',

  /**
   * conflict_writes: Writes that failed with 409 (conflict) or could not be
   * resolved automatically. User-facing via ConflictResolutionDrawer (Sub-Phase 5).
   * NO auto-cleanup — conflicts are real user work.
   */
  CONFLICTS: 'conflict_writes',

  /**
   * uuid_mappings: Maps client-generated UUIDs to server-assigned IDs.
   * Used by sync engine to rewrite foreign-key references in queued writes
   * after a chained resource has been synced.
   */
  UUID_MAPPINGS: 'uuid_mappings',
});

// =============================================================================
// Index Names (canonical constants)
// =============================================================================

/**
 * Index names per object store. Used for efficient querying:
 *   - byScope: filter by (tenantId, userId) tuple for FIFO drain
 *   - byCreatedAt: chronological ordering within scope
 *   - byStatus: filter pending -> in_progress -> ... transitions
 *   - byIdempotencyKey: anti-replay + dedup lookups (Sub-Phase 4)
 *   - byEntityType: scope queries (e.g. "all task writes")
 */
export const INDEXES = Object.freeze({
  BY_SCOPE: 'by_scope',                       // [tenantId, userId]
  BY_CREATED_AT: 'by_created_at',             // single timestamp
  BY_SCOPE_AND_CREATED: 'by_scope_created',   // [tenantId, userId, createdAt] — FIFO
  BY_STATUS: 'by_status',                     // 'pending' | 'in_progress' | 'failed'
  BY_IDEMPOTENCY_KEY: 'by_idempotency_key',   // anti-replay
  BY_ENTITY_TYPE: 'by_entity_type',
});

// =============================================================================
// JSDoc Typedefs for Queue-Entry-Shape (~15 fields)
// =============================================================================

/**
 * QueueEntry — the canonical shape for entries in pending/completed/conflicts.
 *
 * @typedef {Object} QueueEntry
 *
 * IDENTIFICATION & ORDERING
 * @property {string} id - Internal entry ID (UUID, auto-generated on enqueue)
 * @property {string} idempotencyKey - Client-generated UUID, sent as X-Idempotency-Key header
 * @property {string} tenantId - Scope: tenant UUID (matches localStorage.aif_tenant_id)
 * @property {string} userId - Scope: user UUID (matches localStorage.aif_user_id)
 * @property {number} createdAt - Unix ms timestamp of enqueue (FIFO ordering key)
 *
 * REQUEST PAYLOAD
 * @property {('POST'|'PATCH'|'DELETE')} method - HTTP method (writes only)
 * @property {string} url - Full URL the request would have hit
 * @property {object|null} body - Request body (null for DELETE)
 * @property {Object<string,string>} headers - Headers WITHOUT Authorization (re-injected at send time)
 *
 * METADATA & SYNC STATE
 * @property {string} entityType - Semantic type (e.g. 'task', 'activity', 'scouting_finding') for routing
 * @property {('pending'|'in_progress'|'failed'|'conflict')} status - Sync state
 * @property {number} retryCount - Sub-Phase 4 will increment; max 10 before conflict escalation
 *
 * REFERENCE-REWRITING (Sub-Phase 4)
 * @property {Object<string,string>|null} clientRefs - Map of client-UUID -> server-ID for dependency chains
 * @property {object|null} originalState - Pre-write snapshot for rollback / optimistic UI
 *
 * SERVER RESPONSE / CONFLICT DETAIL (populated post-sync)
 * @property {object|null} serverResponse - Response body if successfully synced
 * @property {object|null} conflictDetail - {strategy, serverState, clientState, decision} if conflict
 *
 * MULTIPART / FILE UPLOAD (Sub-Phase 6b)
 * @property {Array<{fieldName: string, blob: Blob, filename?: string, mimeType?: string, size?: number}>|null} blobs - File attachments for multipart/form-data
 * @property {('json'|'multipart')} bodyEncoding - How to reconstruct request body at sync time (default 'json')
 */

// =============================================================================
// Schema Upgrade Logic
// =============================================================================

/**
 * upgradeDatabase: called by idb when DB_VERSION is greater than the existing
 * version in the user's browser. Migrates schema additively.
 *
 * Versioning convention:
 *   - Each version is its own if block (not switch-fall-through)
 *   - Always test on a fresh DB AND on each prior version
 *   - NEVER delete object stores in a migration (data loss); only add or rename via copy
 *
 * @param {IDBPDatabase} db - The database being upgraded
 * @param {number} oldVersion - Version the user currently has (0 if first install)
 * @param {number} newVersion - Target version (== DB_VERSION constant)
 */
function upgradeDatabase(db, oldVersion, newVersion) {
  // ---- Migration to v1 (initial schema) ----
  if (oldVersion < 1) {
    // pending_writes: primary store for queued writes
    const pendingStore = db.createObjectStore(STORES.PENDING, {
      keyPath: 'id',
      autoIncrement: false,
    });
    pendingStore.createIndex(INDEXES.BY_SCOPE, ['tenantId', 'userId'], { unique: false });
    pendingStore.createIndex(INDEXES.BY_CREATED_AT, 'createdAt', { unique: false });
    pendingStore.createIndex(
      INDEXES.BY_SCOPE_AND_CREATED,
      ['tenantId', 'userId', 'createdAt'],
      { unique: false }
    );
    pendingStore.createIndex(INDEXES.BY_STATUS, 'status', { unique: false });
    pendingStore.createIndex(INDEXES.BY_IDEMPOTENCY_KEY, 'idempotencyKey', { unique: true });
    pendingStore.createIndex(INDEXES.BY_ENTITY_TYPE, 'entityType', { unique: false });

    // completed_writes: audit log of successful syncs
    const completedStore = db.createObjectStore(STORES.COMPLETED, {
      keyPath: 'id',
    });
    completedStore.createIndex(INDEXES.BY_SCOPE, ['tenantId', 'userId'], { unique: false });
    completedStore.createIndex(INDEXES.BY_CREATED_AT, 'createdAt', { unique: false });
    completedStore.createIndex(INDEXES.BY_IDEMPOTENCY_KEY, 'idempotencyKey', { unique: true });

    // conflict_writes: failed syncs requiring user action
    const conflictStore = db.createObjectStore(STORES.CONFLICTS, {
      keyPath: 'id',
    });
    conflictStore.createIndex(INDEXES.BY_SCOPE, ['tenantId', 'userId'], { unique: false });
    conflictStore.createIndex(INDEXES.BY_CREATED_AT, 'createdAt', { unique: false });
    conflictStore.createIndex(INDEXES.BY_ENTITY_TYPE, 'entityType', { unique: false });
  }

  // ---- Migration to v2 (A6 Sub-Phase 4 Session 3): uuid_mappings store ----
  // Maps client-generated UUIDs to server-assigned IDs. Used by sync engine
  // to rewrite foreign-key references in queued writes after a chained
  // resource has been synced (e.g., Field synced → server-ID '42', subsequent
  // Activity references 'client-uuid-of-field' which gets rewritten to '42').
  if (oldVersion < 2) {
    if (!db.objectStoreNames.contains('uuid_mappings')) {
      const mapStore = db.createObjectStore('uuid_mappings', { keyPath: 'clientUuid' });
      mapStore.createIndex('by_scope', ['tenantId', 'userId']);
      mapStore.createIndex('by_created_at', 'createdAt');
    }
  }
}

// =============================================================================
// Database Open + Connection Management
// =============================================================================

let dbPromise = null;

/**
 * openDatabase: returns a singleton Promise<IDBPDatabase>.
 * Idempotent: multiple calls share the same connection.
 *
 * The returned database is used by Step 3 operations (enqueue, dequeue, flush).
 *
 * Error handling: if IndexedDB is unavailable (private browsing, quota,
 * corruption), the promise rejects. Callers should handle this gracefully
 * (fallback: send writes directly without queueing — degrade not crash).
 *
 * @returns {Promise<IDBPDatabase>}
 */
export function openDatabase() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction, event) {
        upgradeDatabase(db, oldVersion, newVersion);
      },
      blocked(currentVersion, blockedVersion, event) {
        // Another tab has the DB open at an older version.
        console.warn(
          `[offlineQueue] DB upgrade blocked: current v${currentVersion} → blocked v${blockedVersion}. ` +
            'Another tab may have an older version open. Close other tabs to upgrade.'
        );
      },
      blocking(currentVersion, blockedVersion, event) {
        // This tab is holding an older version that's blocking a newer one.
        // Close our connection so the upgrade can proceed.
        dbPromise = null;
      },
      terminated() {
        // Browser killed the connection (rare but possible). Reset.
        dbPromise = null;
      },
    });
  }
  return dbPromise;
}

/**
 * resetConnection: forces the next openDatabase() to create a fresh connection.
 * Used in tests AND in 'blocking' handler above.
 */
export function resetConnection() {
  dbPromise = null;
}

// =============================================================================
// Scope Helper (read tenantId + userId from localStorage)
// =============================================================================

/**
 * getCurrentScope: read the (tenantId, userId) tuple from localStorage.
 * Returns null if either is missing (caller MUST handle — queue requires both).
 *
 * @returns {{tenantId: string, userId: string} | null}
 */
export function getCurrentScope() {
  try {
    const tenantId = localStorage.getItem('aif_tenant_id');
    const userId = localStorage.getItem('aif_user_id');
    if (!tenantId || !userId) return null;
    return { tenantId, userId };
  } catch {
    return null;
  }
}

// =============================================================================
// OPERATIONS (A6 Sub-Phase 3 Step 3)
// =============================================================================
//
// Public API for queue operations. Built atomically over idb transactions.
//
// Design contract:
//   - enqueue: caller provides idempotencyKey (apiClient generates it)
//   - dequeue: atomic-claim oldest pending, mark status='in_progress'
//   - moveTo: single multi-store transaction (no data-loss window)
//
// Status state-machine for entries in PENDING store:
//   'pending'      — fresh, awaiting sync
//   'in_progress'  — claimed by dequeue, sync underway in some tab
//   'failed'       — sync failed, retry-eligible (Sub-Phase 4 increments retryCount)
//   'conflict'     — escalated (Sub-Phase 5 moves to CONFLICTS store)
//
// Entries in COMPLETED or CONFLICTS stores have no status field changes —
// they are terminal records.

// =============================================================================
// Constants
// =============================================================================

/**
 * Valid status values for entries in the PENDING store.
 */
export const ENTRY_STATUS = Object.freeze({
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  FAILED: 'failed',
  CONFLICT: 'conflict',
});

/**
 * Required fields on every enqueue entry. Validation throws if missing.
 */
const REQUIRED_FIELDS = [
  'id',
  'idempotencyKey',
  'tenantId',
  'userId',
  'method',
  'url',
  'entityType',
];

/**
 * Default values applied during enqueue for fields the caller omits.
 */
function applyEnqueueDefaults(entry) {
  const defaults = {
    body: null,
    headers: {},
    status: ENTRY_STATUS.PENDING,
    retryCount: 0,
    clientRefs: null,
    originalState: null,
    serverResponse: null,
    conflictDetail: null,
    createdAt: Date.now(),
    // A6 Sub-Phase 6b: multipart/FormData support
    blobs: null,
    bodyEncoding: 'json',
  };
  // Spread entry, then replace any undefined values with the defaults.
  // This ensures that explicit undefined from the caller (e.g. body: undefined)
  // falls back to null, not undefined.
  const merged = { ...defaults, ...entry };
  for (const key of Object.keys(defaults)) {
    if (merged[key] === undefined) {
      merged[key] = defaults[key];
    }
  }
  return merged;
}

/**
 * Validate that an entry has all required fields. Throws on violation.
 */
function validateEnqueueEntry(entry) {
  if (!entry || typeof entry !== 'object') {
    throw new TypeError('enqueue: entry must be an object');
  }
  const missing = REQUIRED_FIELDS.filter((f) => !entry[f]);
  if (missing.length > 0) {
    throw new TypeError(
      `enqueue: entry is missing required fields: ${missing.join(', ')}`
    );
  }
  if (!['POST', 'PATCH', 'DELETE'].includes(entry.method)) {
    throw new TypeError(
      `enqueue: method must be POST/PATCH/DELETE, got '${entry.method}'`
    );
  }
  // A6 Sub-Phase 6b: validate optional blobs[] structure
  if (entry.blobs !== null && entry.blobs !== undefined) {
    if (!Array.isArray(entry.blobs)) {
      throw new TypeError(`enqueue: blobs must be Array or null, got ${typeof entry.blobs}`);
    }
    for (const b of entry.blobs) {
      if (!b || typeof b !== 'object') {
        throw new TypeError('enqueue: each blob attachment must be an object');
      }
      if (typeof b.fieldName !== 'string' || !b.fieldName) {
        throw new TypeError('enqueue: blob attachment requires fieldName: string');
      }
      if (!(b.blob instanceof Blob)) {
        throw new TypeError(`enqueue: blob attachment ${b.fieldName} requires blob: Blob`);
      }
    }
  }
  if (entry.bodyEncoding && entry.bodyEncoding !== 'json' && entry.bodyEncoding !== 'multipart') {
    throw new TypeError(`enqueue: bodyEncoding must be 'json' or 'multipart', got '${entry.bodyEncoding}'`);
  }
  if (entry.bodyEncoding === 'multipart' && (!entry.blobs || entry.blobs.length === 0)) {
    throw new TypeError("enqueue: bodyEncoding='multipart' requires at least one blob in blobs[]");
  }
}

// =============================================================================
// enqueue
// =============================================================================

/**
 * Enqueue a write to the PENDING store.
 *
 * Contract:
 *   - Caller provides idempotencyKey (apiClient generates via crypto.randomUUID)
 *   - Caller provides id (typically same as idempotencyKey to keep lookups simple,
 *     but they MAY differ — id is the IndexedDB key, idempotencyKey is the
 *     server-coordination header)
 *   - Other fields get sensible defaults
 *
 * @param {Object} entry - Partial QueueEntry (see typedef in Step 1+2)
 * @returns {Promise<Object>} - The fully-materialized entry as stored
 * @throws {TypeError} if required fields are missing
 */
export async function enqueue(entry) {
  const full = applyEnqueueDefaults(entry);
  validateEnqueueEntry(full);

  // Step 4: capacity gate. Throws QueueFullError if hard-cap exceeded.
  await assertHasCapacity();

  const db = await openDatabase();
  const tx = db.transaction(STORES.PENDING, 'readwrite');
  try {
    await tx.store.put(full);
    await tx.done;
    return full;
  } catch (err) {
    throw new Error(`enqueue failed: ${err.message}`);
  }
}

// =============================================================================
// dequeue — atomic claim
// =============================================================================

/**
 * Atomic-claim the oldest pending entry within scope.
 *
 * Reads via BY_SCOPE_AND_CREATED index (FIFO order), filters to status='pending',
 * marks the claimed entry as 'in_progress' in the SAME transaction. Other tabs
 * doing dequeue concurrently will not see the same entry.
 *
 * @param {{tenantId: string, userId: string}} scope
 * @returns {Promise<Object | null>} - The claimed entry, or null if queue is empty
 */
export async function dequeue(scope) {
  if (!scope || !scope.tenantId || !scope.userId) {
    throw new TypeError('dequeue: scope must have tenantId and userId');
  }

  const db = await openDatabase();
  const tx = db.transaction(STORES.PENDING, 'readwrite');
  const index = tx.store.index(INDEXES.BY_SCOPE_AND_CREATED);

  const lowerBound = [scope.tenantId, scope.userId, -Infinity];
  const upperBound = [scope.tenantId, scope.userId, Infinity];
  const range = IDBKeyRange.bound(lowerBound, upperBound);

  let claimed = null;
  let cursor = await index.openCursor(range, 'next');
  while (cursor) {
    if (cursor.value.status === ENTRY_STATUS.PENDING) {
      claimed = { ...cursor.value, status: ENTRY_STATUS.IN_PROGRESS };
      await cursor.update(claimed);
      break;
    }
    cursor = await cursor.continue();
  }

  await tx.done;
  return claimed;
}

// =============================================================================
// peek (read-only, FIFO)
// =============================================================================

/**
 * Read-only inspection of the queue. Useful for Dev-Panel observability
 * (Sub-Phase 7) and tests.
 *
 * Returns entries in FIFO order (oldest first).
 *
 * @param {{tenantId: string, userId: string}} scope
 * @param {Object} [opts]
 * @param {number} [opts.limit=10] - Maximum entries to return
 * @param {string} [opts.status] - Filter by status (default: all)
 * @returns {Promise<Object[]>}
 */
export async function peek(scope, opts = {}) {
  if (!scope || !scope.tenantId || !scope.userId) {
    throw new TypeError('peek: scope must have tenantId and userId');
  }
  const limit = opts.limit ?? 10;
  const statusFilter = opts.status ?? null;

  const db = await openDatabase();
  const tx = db.transaction(STORES.PENDING, 'readonly');
  const index = tx.store.index(INDEXES.BY_SCOPE_AND_CREATED);

  const lowerBound = [scope.tenantId, scope.userId, -Infinity];
  const upperBound = [scope.tenantId, scope.userId, Infinity];
  const range = IDBKeyRange.bound(lowerBound, upperBound);

  const entries = [];
  let cursor = await index.openCursor(range, 'next');
  while (cursor && entries.length < limit) {
    if (!statusFilter || cursor.value.status === statusFilter) {
      entries.push(cursor.value);
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  return entries;
}

// =============================================================================
// getDepth
// =============================================================================

/**
 * Count entries in PENDING store within scope (or globally).
 *
 * @param {Object} [opts]
 * @param {{tenantId: string, userId: string}} [opts.scope] - Filter by scope
 * @param {string} [opts.status] - Filter by status (default: all)
 * @returns {Promise<number>}
 */
export async function getDepth(opts = {}) {
  const db = await openDatabase();
  const tx = db.transaction(STORES.PENDING, 'readonly');

  if (!opts.scope && !opts.status) {
    const count = await tx.store.count();
    await tx.done;
    return count;
  }

  let count = 0;
  let cursor;
  if (opts.scope) {
    const index = tx.store.index(INDEXES.BY_SCOPE);
    const range = IDBKeyRange.only([opts.scope.tenantId, opts.scope.userId]);
    cursor = await index.openCursor(range);
  } else {
    cursor = await tx.store.openCursor();
  }
  while (cursor) {
    if (!opts.status || cursor.value.status === opts.status) {
      count += 1;
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  return count;
}

// =============================================================================
// moveTo — atomic cross-store move
// =============================================================================

/**
 * Atomically move an entry from one store to another in a single transaction.
 *
 * No data-loss window: if the write to toStore fails, the delete from fromStore
 * also rolls back. IndexedDB transactions abort atomically on any error.
 *
 * @param {string} entryId - The entry's id field
 * @param {string} fromStore - One of STORES.*
 * @param {string} toStore - One of STORES.*
 * @param {Object} [updates] - Optional field updates applied during the move
 * @returns {Promise<Object>} - The entry as written to toStore
 * @throws {Error} if entry not found in fromStore
 */
export async function moveTo(entryId, fromStore, toStore, updates = {}) {
  if (!entryId || !fromStore || !toStore) {
    throw new TypeError('moveTo: entryId, fromStore, toStore are required');
  }
  if (fromStore === toStore) {
    throw new TypeError('moveTo: fromStore and toStore must differ');
  }

  const db = await openDatabase();
  const tx = db.transaction([fromStore, toStore], 'readwrite');
  const fromOs = tx.objectStore(fromStore);
  const toOs = tx.objectStore(toStore);

  const existing = await fromOs.get(entryId);
  if (!existing) {
    await tx.done.catch(() => {});
    throw new Error(`moveTo: entry '${entryId}' not found in '${fromStore}'`);
  }

  const merged = { ...existing, ...updates };
  await toOs.put(merged);
  await fromOs.delete(entryId);
  await tx.done;
  return merged;
}

// =============================================================================
// Ergonomic wrappers over moveTo
// =============================================================================

/**
 * Move a successfully-synced entry from PENDING to COMPLETED.
 * Typically called by Sub-Phase 4 sync engine on 2xx response.
 *
 * @param {string} entryId
 * @param {object} [serverResponse] - Response body from the server
 * @returns {Promise<Object>}
 */
export async function markCompleted(entryId, serverResponse = null) {
  return moveTo(entryId, STORES.PENDING, STORES.COMPLETED, {
    serverResponse,
    completedAt: Date.now(),
  });
}

/**
 * Mark an entry as failed. Stays in PENDING with status='failed' and
 * incremented retryCount. Sub-Phase 4 sync engine decides when to retry
 * vs escalate to conflict.
 *
 * Does NOT use moveTo — failed entries stay in PENDING for retry-eligibility.
 *
 * @param {string} entryId
 * @param {Error|string} [error] - Error context for debugging
 * @returns {Promise<Object>}
 */
export async function markFailed(entryId, error = null) {
  const db = await openDatabase();
  const tx = db.transaction(STORES.PENDING, 'readwrite');
  const existing = await tx.store.get(entryId);
  if (!existing) {
    await tx.done.catch(() => {});
    throw new Error(`markFailed: entry '${entryId}' not found in PENDING`);
  }
  const updated = {
    ...existing,
    status: ENTRY_STATUS.FAILED,
    retryCount: (existing.retryCount ?? 0) + 1,
    lastError: error ? String(error.message || error) : null,
    lastFailedAt: Date.now(),
  };
  await tx.store.put(updated);
  await tx.done;
  return updated;
}

/**
 * Move a conflicted entry from PENDING to CONFLICTS store.
 * Called when sync produces a 409 response or 3WM fails.
 *
 * @param {string} entryId
 * @param {object} conflictDetail - {strategy, serverState, clientState, decision}
 * @returns {Promise<Object>}
 */
export async function markConflict(entryId, conflictDetail) {
  return moveTo(entryId, STORES.PENDING, STORES.CONFLICTS, {
    conflictDetail,
    conflictedAt: Date.now(),
  });
}

// =============================================================================
// resetFailedToPending — retry eligibility for next sync cycle
// =============================================================================

/**
 * Reset all 'failed' entries within scope back to 'pending' so they are
 * eligible for dequeue on the next sync cycle. Called at the START of each
 * sync cycle by syncEngine.js.
 *
 * @param {{tenantId: string, userId: string}} scope
 * @returns {Promise<number>} Count of entries reset
 */
export async function resetFailedToPending(scope) {
  if (!scope || !scope.tenantId || !scope.userId) {
    throw new TypeError('resetFailedToPending: scope must have tenantId and userId');
  }
  const db = await openDatabase();
  const tx = db.transaction(STORES.PENDING, 'readwrite');
  const index = tx.store.index(INDEXES.BY_SCOPE);
  const range = IDBKeyRange.only([scope.tenantId, scope.userId]);

  let count = 0;
  let cursor = await index.openCursor(range);
  while (cursor) {
    if (cursor.value.status === ENTRY_STATUS.FAILED) {
      const updated = { ...cursor.value, status: ENTRY_STATUS.PENDING };
      await cursor.update(updated);
      count += 1;
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  return count;
}

// =============================================================================
// clear (admin/debug)
// =============================================================================

/**
 * Delete entries. ADMIN/DEBUG ONLY — production code should not call this.
 *
 * @param {Object} [opts]
 * @param {string} [opts.store=STORES.PENDING] - Which store to clear
 * @param {{tenantId: string, userId: string}} [opts.scope] - Optional scope filter
 * @returns {Promise<number>} - Number of entries deleted
 */
export async function clear(opts = {}) {
  const store = opts.store ?? STORES.PENDING;
  const db = await openDatabase();
  const tx = db.transaction(store, 'readwrite');

  if (!opts.scope) {
    await tx.store.clear();
    await tx.done;
    return -1;
  }

  const index = tx.store.index(INDEXES.BY_SCOPE);
  const range = IDBKeyRange.only([opts.scope.tenantId, opts.scope.userId]);
  let count = 0;
  let cursor = await index.openCursor(range);
  while (cursor) {
    await cursor.delete();
    count += 1;
    cursor = await cursor.continue();
  }
  await tx.done;
  return count;
}

// =============================================================================
// UUID Mapping API (A6 Sub-Phase 4 Session 3)
// =============================================================================
//
// Maps client-generated UUIDs (used in offline writes) to server-assigned IDs
// (returned after sync). Enables reference-rewriting in chained writes:
//   1. Offline: Field {id: 'client-uuid-1'} queued
//   2. Offline: Activity {field_id: 'client-uuid-1'} queued
//   3. Online: Field synced → server returns {id: '42'}
//   4. mapUuid('client-uuid-1', '42', scope) records the mapping
//   5. Activity dequeued: syncEngine resolves 'client-uuid-1' → '42' before send
//
// Retention: aligned with idempotency-key TTL (30 days). Older mappings can
// be cleaned via clearMappings(scope) (called by Sub-Phase 7 housekeeping).

/**
 * Record a UUID → server-ID mapping. Idempotent (overwrites on conflict).
 *
 * @param {string} clientUuid
 * @param {string} serverId
 * @param {{tenantId, userId}} scope
 * @returns {Promise<{clientUuid, serverId}>}
 */
export async function mapUuid(clientUuid, serverId, scope) {
  if (typeof clientUuid !== 'string' || clientUuid.length === 0) {
    throw new TypeError('mapUuid: clientUuid must be non-empty string');
  }
  if (typeof serverId !== 'string' || serverId.length === 0) {
    throw new TypeError('mapUuid: serverId must be non-empty string');
  }
  if (!scope || !scope.tenantId || !scope.userId) {
    throw new TypeError('mapUuid: scope must have tenantId and userId');
  }
  const db = await openDatabase();
  const record = {
    clientUuid,
    serverId,
    tenantId: scope.tenantId,
    userId: scope.userId,
    createdAt: Date.now(),
  };
  const tx = db.transaction(STORES.UUID_MAPPINGS, 'readwrite');
  await tx.store.put(record);
  await tx.done;
  return record;
}

/**
 * Resolve a clientUuid to its serverId. Returns null if not yet mapped.
 *
 * @param {string} clientUuid
 * @returns {Promise<string | null>}
 */
export async function resolveUuid(clientUuid) {
  if (typeof clientUuid !== 'string' || clientUuid.length === 0) {
    return null;
  }
  const db = await openDatabase();
  const tx = db.transaction(STORES.UUID_MAPPINGS, 'readonly');
  const record = await tx.store.get(clientUuid);
  await tx.done;
  return record ? record.serverId : null;
}

/**
 * Bulk-resolve multiple clientUuids. Returns map of {clientUuid: serverId or null}.
 *
 * @param {string[]} clientUuids
 * @returns {Promise<Record<string, string | null>>}
 */
export async function resolveUuids(clientUuids) {
  const result = {};
  for (const uuid of clientUuids) {
    result[uuid] = await resolveUuid(uuid);
  }
  return result;
}

/**
 * List all mappings for a scope. Used by tests and housekeeping.
 *
 * @param {{tenantId, userId}} scope
 * @returns {Promise<Array>}
 */
export async function listMappings(scope) {
  if (!scope || !scope.tenantId || !scope.userId) {
    throw new TypeError('listMappings: scope required');
  }
  const db = await openDatabase();
  const tx = db.transaction(STORES.UUID_MAPPINGS, 'readonly');
  const index = tx.store.index('by_scope');
  const range = IDBKeyRange.only([scope.tenantId, scope.userId]);
  const result = await index.getAll(range);
  await tx.done;
  return result;
}

/**
 * Clear all mappings for a scope. Used by housekeeping + tests.
 *
 * @param {{tenantId, userId}} [scope] - If omitted, clears all
 * @returns {Promise<number>} count cleared
 */
export async function clearMappings(scope) {
  const db = await openDatabase();
  const tx = db.transaction(STORES.UUID_MAPPINGS, 'readwrite');
  let count = 0;
  if (scope) {
    const index = tx.store.index('by_scope');
    const range = IDBKeyRange.only([scope.tenantId, scope.userId]);
    let cursor = await index.openCursor(range);
    while (cursor) {
      await cursor.delete();
      count += 1;
      cursor = await cursor.continue();
    }
  } else {
    count = await tx.store.count();
    await tx.store.clear();
  }
  await tx.done;
  return count;
}

// =============================================================================
// Conflict Resolution Accessors (A6 Sub-Phase 5 Block 5-MAIN-b)
// =============================================================================
//
// The CONFLICTS store holds entries escalated by the sync engine (Tier 3:
// true field conflict + clock drift). The ConflictResolutionDrawer reads them
// and lets the user choose: accept server, keep mine (force re-send), discard.

/**
 * List all entries currently in the CONFLICTS store for a scope.
 * @param {{tenantId, userId}} scope
 * @returns {Promise<Array>}
 */
export async function listConflicts(scope) {
  if (!scope || !scope.tenantId || !scope.userId) {
    throw new TypeError('listConflicts: scope must have tenantId and userId');
  }
  const db = await openDatabase();
  const tx = db.transaction(STORES.CONFLICTS, 'readonly');
  const index = tx.store.index(INDEXES.BY_SCOPE);
  const range = IDBKeyRange.only([scope.tenantId, scope.userId]);
  const result = await index.getAll(range);
  await tx.done;
  return result;
}

/**
 * User accepts server version (their change is dropped, server value kept).
 * Deletes the entry from CONFLICTS.
 * @param {string} entryId
 * @returns {Promise<boolean>}
 */
export async function resolveConflictAcceptServer(entryId) {
  const db = await openDatabase();
  const tx = db.transaction(STORES.CONFLICTS, 'readwrite');
  const existing = await tx.store.get(entryId);
  if (existing) {
    await tx.store.delete(entryId);
  }
  await tx.done;
  return !!existing;
}

/**
 * User discards the conflict (semantically same as accept-server: client
 * change is not applied). Kept distinct for clearer call-site intent + audit.
 * @param {string} entryId
 * @returns {Promise<boolean>}
 */
export async function resolveConflictDiscard(entryId) {
  return resolveConflictAcceptServer(entryId);
}

/**
 * User keeps their version: move the entry back to PENDING as a FORCE write.
 * Clears knownUpdatedAt + originalState so the sync engine re-sends WITHOUT
 * the If-Unmodified-Since precondition (intentional overwrite). Reuses the
 * proven Tier-2 force path.
 * @param {string} entryId
 * @returns {Promise<Object>} the re-queued entry
 */
export async function resolveConflictKeepMine(entryId) {
  const db = await openDatabase();
  const tx = db.transaction([STORES.CONFLICTS, STORES.PENDING], 'readwrite');
  const conflictStore = tx.objectStore(STORES.CONFLICTS);
  const pendingStore = tx.objectStore(STORES.PENDING);
  const existing = await conflictStore.get(entryId);
  if (!existing) {
    await tx.done.catch(() => {});
    throw new Error(`resolveConflictKeepMine: entry '${entryId}' not found in CONFLICTS`);
  }
  const forced = {
    ...existing,
    status: ENTRY_STATUS.PENDING,
    knownUpdatedAt: null,
    originalState: null,
    conflictDetail: null,
    retryCount: 0,
    forcedAt: Date.now(),
  };
  await pendingStore.put(forced);
  await conflictStore.delete(entryId);
  await tx.done;
  return forced;
}
